/**
 * The back office's working context: for whom, from where, over which period.
 *
 * One answer to "where am I?" for the whole session — the customer being
 * served, the company selling, the location, point of sale and depot whose
 * stock is shown, and the month the sales lists are read over. The header
 * shows it on every page, and the screens it drives read it from here:
 *
 * - the catalogue's consultation takes company, city, point of sale and
 *   depot (`useScope`, injected into `catalogRoutes`);
 * - the sales lists take the customer (`useClient`, into `ordersRoutes`).
 *
 * It lives in the application, not in a `core-*` package, because every
 * field is a business concept and `core-*` may name none; and not in a
 * feature, because a feature may not reach a sibling. The features declare
 * the shape they need and this file is where the shapes meet.
 *
 * Remembered per browser (`localStorage`), as a convenience: the context is
 * a preference about the view, never an authorisation — the server scopes
 * every request on its own, whatever is stored here.
 */

import { createContext, useContext } from "react";

export interface WorkspaceClient {
  id: string;
  code: string;
  name: string;
}

export interface Workspace {
  client: WorkspaceClient | null;
  /** Empty means every company. */
  companyCode: string;
  city: string;
  pointOfSaleCode: string;
  siteCode: string;
  /** `YYYY-MM`. */
  period: string;
  /** Which articles lists show: active ones, inactive ones, or all. */
  active: ActiveFilter;
}

export type ActiveFilter = "all" | "active" | "inactive";

const ACTIVE_FILTERS: readonly string[] = ["all", "active", "inactive"];

export const STORAGE_KEY = "hardauto.workspace";

export function currentPeriod(today: Date = new Date()): string {
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
}

export function emptyWorkspace(today?: Date): Workspace {
  return {
    client: null,
    companyCode: "",
    city: "",
    pointOfSaleCode: "",
    siteCode: "",
    period: currentPeriod(today),
    active: "all",
  };
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * What was stored, read defensively: a field of the wrong type is dropped
 * rather than trusted, because the storage is the browser's and anything may
 * be in it — another version of this application, or a hand edit.
 */
export function readWorkspace(storage: Pick<Storage, "getItem"> | null): Workspace {
  const fallback = emptyWorkspace();
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return fallback;
    const record = parsed as Record<string, unknown>;
    const client = record.client as Record<string, unknown> | null | undefined;
    return {
      client:
        client && text(client.id)
          ? { id: text(client.id), code: text(client.code), name: text(client.name) }
          : null,
      companyCode: text(record.companyCode),
      city: text(record.city),
      pointOfSaleCode: text(record.pointOfSaleCode),
      siteCode: text(record.siteCode),
      period: /^\d{4}-\d{2}$/u.test(text(record.period)) ? text(record.period) : fallback.period,
      active: ACTIVE_FILTERS.includes(text(record.active))
        ? (text(record.active) as ActiveFilter)
        : fallback.active,
    };
  } catch {
    // Private windows and blocked storage throw on access; the context then
    // simply starts empty.
    return fallback;
  }
}

export function writeWorkspace(storage: Pick<Storage, "setItem"> | null, value: Workspace): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Same as above: a context that is not remembered still works.
  }
}

export function browserStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export interface WorkspaceValue {
  workspace: Workspace;
  update: (patch: Partial<Workspace>) => void;
  reset: () => void;
  /** Whether the context panel is open. Owned here so any screen can open it. */
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
}

export const WorkspaceContext = createContext<WorkspaceValue | null>(null);

export function useWorkspace(): WorkspaceValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error("useWorkspace must be used inside <WorkspaceProvider>.");
  return value;
}

/** The catalogue's view of the context — the shape `feature-catalog` declares. */
export function useCatalogScope() {
  const { workspace, update } = useWorkspace();
  return {
    scope: {
      companyCode: workspace.companyCode,
      city: workspace.city,
      pointOfSaleCode: workspace.pointOfSaleCode,
      siteCode: workspace.siteCode,
      active: workspace.active,
    },
    setScope: (
      patch: Partial<Pick<Workspace, "companyCode" | "city" | "pointOfSaleCode" | "siteCode">>,
    ) => {
      update(patch);
    },
  };
}

/** The sales lists' view: the customer, or nobody. */
export function useScopeClient(): { id: string; label: string } | null {
  const { workspace } = useWorkspace();
  return workspace.client
    ? { id: workspace.client.id, label: workspace.client.name || workspace.client.code }
    : null;
}
